import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import Button from "../components/Button";
import Page from "@/components/Page";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useGetPlacesByName } from "@/hooks/usePlaces";
import PlaceSearchItem from "@/components/PlaceSearchItem";
import { encodeBase32 } from "geohashing";
import { sendGA4PageView } from "@/lib/analytics";
import { captureException } from "@sentry/react";
import { debounce } from "lodash";
import { trackEvent } from "@/lib/amplitude";

export default function TagPlace() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [placeUid, setPlaceUid] = useState<number | null>(null);
  const [geohashLocation, setGeoHashLocation] = useState<string | null>(null);
  const { data } = useGetPlacesByName(searchTerm, geohashLocation);
  const hasTrackedPageView = useRef(false);

  const requestLocation = () => {
    trackEvent("geolocation_requested", {});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const geohash = encodeBase32(
            position.coords.latitude,
            position.coords.longitude,
            5, // 4.9km cell size
          );
          setGeoHashLocation(geohash);
        },
        (error) => {
          console.error("Error getting location:", error);
          captureException(error);
          trackEvent("geolocation_denied", {
            errorCode: error.code,
            errorMessage: error.message,
          });
        },
      );
    } else {
      const error = new Error("Geolocation is not supported by this browser.");
      console.error(error.message);
      captureException(error);
    }
  };

  useEffect(() => {
    sendGA4PageView(window.location.pathname + window.location.search);
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackEvent("tag_place_viewed", {});
    }
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          requestLocation();
        } else if (result.state === "prompt") {
          requestLocation();
        } else if (result.state === "denied") {
          alert(
            "Location access has been denied. Please enable location access in your browser settings.",
          );
        }
      });
    } else {
      requestLocation();
    }
  }, []);

  const updateSearchTerm = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      if (e.target.value.length > 2) {
        trackEvent("restaurant_searched", {
          searchTerm: e.target.value,
        });
      }
    }, 1000),
    [],
  );

  return (
    <Page
      title={t("tag-place.title")}
      lngChange={true}
      action={
        <Button
          onPress={() => {
            if (placeUid == null) return;
            window.localStorage.setItem("placeRef", placeUid.toString());
            window.localStorage.setItem("placeRefTimestamp", Date.now().toString());
            navigate(`/upload`);
          }}
        >
          {t("tag-place.button")}
        </Button>
      }
    >
      <div className="relative w-full md:grow-0">
        <Search
          className="bg-blue absolute left-2.5 top-2.5 h-4 w-4 text-white"
          color="rgb(255, 92, 92)"
        />
        <Input
          type="search"
          placeholder="Search..."
          className="w-full rounded-lg bg-background pl-8 text-black text-white lg:w-full"
          onChange={updateSearchTerm}
        />
      </div>
      <div className="space-y-2 pt-4">
        {data
          ?.filter((value) =>
            searchTerm
              .toLowerCase()
              .split(" ")
              .every((el) => {
                if (!value.name || !value.address) {
                  return false;
                }

                // Convert both fields to lowercase for case-insensitive comparison.
                const lowerName = value.name.toLowerCase();
                const lowerAddress = value.address.toLowerCase();

                return lowerName.includes(el) || lowerAddress.includes(el);
              }),
          )
          .map((place) => (
            <PlaceSearchItem
              place={place}
              key={place.id}
              onClick={() => {
                setPlaceUid(place.id);
                trackEvent("restaurant_selected", {
                  restaurantId: place.id,
                  restaurantName: place.name,
                  source: "manual",
                });
              }}
              selected={placeUid === place.id}
            />
          ))}
      </div>
    </Page>
  );
}
